import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Bot, Pointer, XCircle, AlertCircle, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getSafeImageUrl } from "@/utils/sanitizeUrl";

export type BubbleType = "modal" | "toast" | "chatbot" | "tooltip";
export type BubbleShape = "rounded" | "square" | "pill";
export type BubbleTrigger = "immediate" | "scroll" | "delay";
export type BubblePosition = "center" | "top-right" | "top-left" | "bottom-right" | "bottom-left" | "top-center" | "bottom-center";
export type BubbleStatus = "draft" | "published";

interface Bubble {
  id: string;
  type: BubbleType;
  title: string;
  message: string;
  shape: BubbleShape;
  colorClass: string;
  triggerMode: BubbleTrigger;
  triggerDelay?: number;
  position: BubblePosition;
  active: boolean;
  status: BubbleStatus;
  targetPage: string; 
  imageUrl?: string;
  linkUrl?: string;
  buttonText?: string;
  startDate?: string;
  endDate?: string;
  bubbleSize?: string;
  textFormat?: string;
}

const SystemBubblesManager = () => {
  const [bubbles, setBubbles] = useState<Bubble[]>([]);
  const [visibleBubbles, setVisibleBubbles] = useState<string[]>([]);
  const location = useLocation();

  useEffect(() => {
    const loadBubbles = () => {
      const stored = localStorage.getItem("kaynin_bulles");
      if (stored) {
        const parsed: Bubble[] = JSON.parse(stored);
        const activePublished = parsed.filter(b => {
           if (!b.active || b.status !== "published") return false;
           
           const now = new Date().getTime();
           if (b.startDate && new Date(b.startDate).getTime() > now) return false;
           if (b.endDate && new Date(b.endDate).getTime() < now) return false;
           
           return true;
        });
        setBubbles(activePublished);
      }
    };
    
    loadBubbles();
    window.addEventListener('storage', loadBubbles);
    window.addEventListener('kaynin_bulles_updated', loadBubbles);
    return () => {
      window.removeEventListener('storage', loadBubbles);
      window.removeEventListener('kaynin_bulles_updated', loadBubbles);
    };
  }, []);

  useEffect(() => {
    const closedInSession = JSON.parse(sessionStorage.getItem("kaynin_closed_bubbles") || "[]");
    
    bubbles.forEach(bubble => {
      if (closedInSession.includes(bubble.id)) return;
      
      const isTargetPage = bubble.targetPage === "all" || bubble.targetPage === location.pathname;
      if (!isTargetPage) {
        // Cacher si on change de page et ce n'est pas ciblé
        setVisibleBubbles(prev => prev.filter(id => id !== bubble.id));
        return;
      }
      
      if (bubble.triggerMode === "immediate") {
        setVisibleBubbles(prev => prev.includes(bubble.id) ? prev : [...prev, bubble.id]);
      } else if (bubble.triggerMode === "delay" && bubble.triggerDelay) {
        const timer = setTimeout(() => {
          setVisibleBubbles(prev => {
             const currClosed = JSON.parse(sessionStorage.getItem("kaynin_closed_bubbles") || "[]");
             if (currClosed.includes(bubble.id)) return prev;
             return prev.includes(bubble.id) ? prev : [...prev, bubble.id];
          });
        }, bubble.triggerDelay * 1000);
        return () => clearTimeout(timer);
      } else if (bubble.triggerMode === "scroll") {
        const handleScroll = () => {
          if (window.scrollY > 200) {
            setVisibleBubbles(prev => prev.includes(bubble.id) ? prev : [...prev, bubble.id]);
            window.removeEventListener("scroll", handleScroll);
          }
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
      }
    });
  }, [bubbles, location.pathname]);


  const handleClose = (id: string) => {
    setVisibleBubbles(prev => prev.filter(bId => bId !== id));
    const closed = JSON.parse(sessionStorage.getItem("kaynin_closed_bubbles") || "[]");
    sessionStorage.setItem("kaynin_closed_bubbles", JSON.stringify([...closed, id]));
  };

  const getPositionClasses = (position: BubblePosition) => {
    switch (position) {
      case "center": return "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2";
      case "top-right": return "top-20 right-8"; // Offset navbar
      case "top-left": return "top-20 left-8";
      case "bottom-right": return "bottom-8 right-8";
      case "bottom-left": return "bottom-8 left-8";
      case "top-center": return "top-20 left-1/2 -translate-x-1/2";
      case "bottom-center": return "bottom-8 left-1/2 -translate-x-1/2";
      default: return "bottom-8 right-8";
    }
  };

  const getShapeClass = (shape: BubbleShape) => {
    switch (shape) {
      case "pill": return "rounded-full";
      case "square": return "rounded-none";
      default: return "rounded-2xl";
    }
  };

  if (visibleBubbles.length === 0) return null;

  return (
    <>
      {bubbles.filter(b => visibleBubbles.includes(b.id)).map(bubble => {
        const isDarkBase = bubble.colorClass !== "bg-background" && bubble.colorClass !== "bg-muted";
        const textColor = isDarkBase ? "text-white" : "text-foreground";
        const positionClass = getPositionClasses(bubble.position);
        const shapeClass = getShapeClass(bubble.shape);
        const hasBackdrop = bubble.type === "modal" || bubble.position === "center";

        if (bubble.type === "chatbot") {
           return (
             <div key={bubble.id} className="relative z-[150] kaynin-bubble">
               <div className={`fixed ${positionClass} ${shapeClass} flex items-center justify-center w-16 h-16 shadow-2xl ${bubble.colorClass} ${textColor} cursor-pointer md:hover:scale-105 transition-all duration-300 animate-in zoom-in group`} onClick={() => handleClose(bubble.id)}>
                 <Bot className="w-8 h-8" />
                 <div className={`absolute ${bubble.position.includes('left') ? 'left-[calc(100%+1rem)]' : 'right-[calc(100%+1rem)]'} top-1/2 -translate-y-1/2 bg-background ${isDarkBase ? 'text-foreground' : ''} border border-border/50 shadow-xl p-4 rounded-2xl whitespace-nowrap min-w-[250px] opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none group-hover:translate-x-0 ${bubble.position.includes('left') ? '-translate-x-2' : 'translate-x-2'}`}>
                     {bubble.title && <p className="text-sm font-bold mb-1 flex items-center gap-2"><MessageCircle className="w-4 h-4 text-primary"/>{bubble.title}</p>}
                     {bubble.message && <div className={`text-xs text-muted-foreground whitespace-normal leading-relaxed text-balance ${bubble.textFormat === 'bold' || bubble.textFormat === 'bold-italic' ? 'font-bold' : ''} ${bubble.textFormat === 'italic' || bubble.textFormat === 'bold-italic' ? 'italic' : ''}`} dangerouslySetInnerHTML={{ __html: bubble.message }} />}
                     {bubble.linkUrl && <a href={bubble.linkUrl} target="_blank" className="text-xs text-primary underline mt-1 block">Découvrir le lien</a>}
                     <div className="mt-3 text-[10px] text-center bg-primary/10 text-primary py-1 rounded-md font-bold uppercase tracking-wider">Cliquez pour ignorer</div>
                 </div>
               </div>
             </div>
           );
        }

        if (bubble.type === "tooltip") {
           return (
             <div key={bubble.id} className="relative z-[150] kaynin-bubble">
               <div className={`fixed ${positionClass} flex items-center gap-3 animate-in fade-in slide-in-from-bottom-2 group`}>
                  <div className={`w-10 h-10 ${shapeClass} ${bubble.colorClass} ${textColor} flex items-center justify-center shadow-lg hover:animate-pulse cursor-pointer transition-transform md:hover:scale-105`}>
                    <Pointer className="w-5 h-5" />
                  </div>
                  <div className={`bg-background text-foreground border border-border/50 shadow-xl p-3 ${shapeClass === 'rounded-full' ? 'rounded-2xl' : shapeClass} text-sm max-w-[250px] space-y-1`}>
                     <p className="font-bold flex justify-between items-center">{bubble.title || 'Notification'} <Button variant="ghost" size="icon" onClick={() => handleClose(bubble.id)} className="h-5 w-5 -mr-1 -mt-1 rounded-full opacity-50 hover:opacity-100"><XCircle className="w-4 h-4" /></Button></p>
                     {bubble.message && <div className={`text-muted-foreground text-xs leading-relaxed text-balance ${bubble.textFormat === 'bold' || bubble.textFormat === 'bold-italic' ? 'font-bold' : ''} ${bubble.textFormat === 'italic' || bubble.textFormat === 'bold-italic' ? 'italic' : ''}`} dangerouslySetInnerHTML={{ __html: bubble.message }} />}
                     {bubble.imageUrl && getSafeImageUrl(bubble.imageUrl) && <img src={getSafeImageUrl(bubble.imageUrl)} className="w-full h-auto mt-2 rounded" alt="Tooltip" />}
                     {bubble.linkUrl && <a href={bubble.linkUrl} target="_blank" className="text-xs text-primary underline mt-1 block">Cliquez ici</a>}
                  </div>
               </div>
             </div>
           );
        }

         let widthClass = "max-w-sm";
         if (bubble.bubbleSize === "sm") widthClass = "max-w-xs";
         else if (bubble.bubbleSize === "lg") widthClass = "max-w-md";
         else if (bubble.bubbleSize === "xl") widthClass = "max-w-xl";

         const textClasses = `text-balance ${bubble.bubbleSize === 'lg' || bubble.bubbleSize === 'xl' ? 'text-base' : 'text-sm'} ${bubble.textFormat === 'bold' || bubble.textFormat === 'bold-italic' ? 'font-bold' : ''} ${bubble.textFormat === 'italic' || bubble.textFormat === 'bold-italic' ? 'italic' : ''}`;

         return (
           <div key={bubble.id} className="relative z-[150] kaynin-bubble">
             {hasBackdrop && (
               <div className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity animate-in fade-in" onClick={() => handleClose(bubble.id)} />
             )}
             <div className={`fixed ${positionClass} ${shapeClass} ${bubble.colorClass} ${textColor} p-6 shadow-2xl ${widthClass} w-[calc(100%-2rem)] md:w-full transition-all animate-in zoom-in-95 duration-300 border border-white/10 max-h-[85vh] overflow-y-auto overflow-x-hidden custom-scrollbar ${hasBackdrop ? 'scale-100' : ''}`}>
                <div className="flex justify-between items-start mb-3 relative z-20">
                  {bubble.title ? (
                    <h4 className="font-display font-bold text-lg flex items-center gap-2">
                      {bubble.type === 'modal' ? <AlertCircle className="w-5 h-5 opacity-80" /> : <MessageCircle className="w-5 h-5 opacity-80" />}
                      {bubble.title}
                    </h4>
                  ) : <div />}
                  <button onClick={(e) => { e.preventDefault(); handleClose(bubble.id); }} className="opacity-50 hover:opacity-100 transition-opacity p-1 hover:bg-black/10 rounded-full">
                    <XCircle className="w-5 h-5" />
                  </button>
                </div>
                
                {React.createElement(
                  bubble.linkUrl ? "a" : "div",
                  bubble.linkUrl ? { href: bubble.linkUrl, target: "_blank", rel: "noopener noreferrer", className: "block cursor-pointer hover:opacity-90 transition-opacity" } : {},
                  <>
                    {bubble.imageUrl && getSafeImageUrl(bubble.imageUrl) && (
                      <div className={`w-full rounded overflow-hidden border border-border/50 bg-black/5 flex items-center justify-center ${bubble.message ? 'mb-3' : ''}`}>
                        <img src={getSafeImageUrl(bubble.imageUrl)} className="w-full h-auto object-contain max-h-[60vh]" alt="Bulle media" />
                      </div>
                    )}
                    {bubble.message && (
                      <div 
                        className={`font-body ${isDarkBase ? 'text-white/80' : 'text-muted-foreground'} leading-relaxed mb-1 ${textClasses}`}
                        dangerouslySetInnerHTML={{ __html: bubble.message }}
                      />
                    )}
                  </>
                )}
                {bubble.type === "modal" && bubble.buttonText !== "" && (
                  <div className="mt-6 flex justify-end gap-3 w-full relative z-20">
                    <Button variant={isDarkBase ? "secondary" : "default"} onClick={() => handleClose(bubble.id)} className="w-full sm:w-auto font-bold">{bubble.buttonText || "J'ai compris"}</Button>
                  </div>
                )}
             </div>
           </div>
        );
      })}
    </>
  );
};

export default SystemBubblesManager;
